import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Typography,
  Tabs,
  Radio,
  Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, TeamOutlined, SettingOutlined, SearchOutlined } from '@ant-design/icons';
import { userService } from '../services/userService';
import { userApi } from '../services/user';
import { useAuthStore } from '../stores/authStore';
import { usePermissionStore } from '../stores/permissionStore';
import { PermissionGuard } from '../components/PermissionGuard';
import { billingService, type BillingPlan, type SubscriptionInfo } from '../services/billing';
import { featureService, type ClientFeature } from '../services/featureService';

const { Option } = Select;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

interface User {
  id: number;
  username: string;
  email: string;
  user_type: 'admin' | 'client' | 'sub';
  status: 'active' | 'disabled';
  client_id: number;
  parent_id?: number;
  raw_password?: string;
  telegram?: string;
  wechat?: string;
  created_at: string;
  updated_at: string;
}

interface SubAccount extends User {
  parent_username?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<SubAccount[]>([]);
  const [clients, setClients] = useState<SubAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<SubAccount | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [activeTab, setActiveTab] = useState('sub-accounts');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  // 搜索
  const [searchValue, setSearchValue] = useState('');
  const [searchId, setSearchId] = useState<number | undefined>(undefined);
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(undefined);
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [planModalLoading, setPlanModalLoading] = useState(false);
  const [planTargetClient, setPlanTargetClient] = useState<SubAccount | null>(null);
  const [availablePlans, setAvailablePlans] = useState<BillingPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [planChangeReason, setPlanChangeReason] = useState('');
  const [currentSubscription, setCurrentSubscription] = useState<SubscriptionInfo | null>(null);
  const [featureModalVisible, setFeatureModalVisible] = useState(false);
  const [featureModalLoading, setFeatureModalLoading] = useState(false);
  const [featureTargetClient, setFeatureTargetClient] = useState<SubAccount | null>(null);
  const [clientFeatures, setClientFeatures] = useState<any[]>([]);

  const currentUser = useAuthStore(state => state.user);
  const { hasPermission } = usePermissionStore();

  // 用于取消请求的 AbortController
  const abortControllerRef = useRef<AbortController | null>(null);
  // 用于防抖的定时器
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 获取用户列表
  const fetchUsers = useCallback(async (page = 1, pageSize = 10, sid?: number, keyword?: string) => {
    console.log('fetchUsers called at:', new Date().toISOString());

    // 清除之前的防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();

    // 设置防抖延迟
    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params: any = { page, size: pageSize };
        if (selectedClientId) params.client_id = selectedClientId;
        if (sid) params.search_id = sid;
        else if (keyword) params.keyword = keyword;
        const response = await userService.getSubAccounts(params);
        if (!abortControllerRef.current?.signal.aborted) {
          setUsers(response.list);
          setPagination({ current: page, pageSize, total: response.total });
          setLoading(false);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('获取用户列表失败:', error);
          message.error('获取用户列表失败');
          setLoading(false);
        }
      }
    }, 100);
  }, [selectedClientId]);

  // 获取客户列表（系统管理员专用）
  const fetchClients = useCallback(async (page = 1, pageSize = 10, sid?: number, keyword?: string) => {
    console.log('fetchClients called at:', new Date().toISOString());
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params: any = { page, size: pageSize, user_type: 'client' };
        if (sid) params.search_id = sid;
        else if (keyword) params.keyword = keyword;
        const response = await userApi.getClientUsers(params);
        if (!abortControllerRef.current?.signal.aborted) {
          setClients(response.data?.list || []);
          setPagination({ current: page, pageSize, total: response.data?.total || 0 });
          setLoading(false);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('获取客户列表失败:', error);
          message.error('获取客户列表失败');
          setLoading(false);
        }
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (currentUser?.user_type === 'admin') {
      // 系统管理员显示客户管理
      setActiveTab('clients');
      // 直接调用API，避免依赖fetchClients
      const initClients = async () => {
        setLoading(true);
        try {
          const response = await userApi.getClientUsers({
            page: 1,
            size: pagination.pageSize,
            user_type: 'client',
          });
          setClients(response.data?.list || []);
          setPagination({
            current: 1,
            pageSize: pagination.pageSize,
            total: response.data?.total || 0,
          });
          setLoading(false);
        } catch (error: any) {
          console.error('获取客户列表失败:', error);
          message.error('获取客户列表失败');
          setLoading(false);
        }
      };
      initClients();
    } else {
      // 客户账号显示子账号管理
      setActiveTab('sub-accounts');
      // 直接调用API，避免依赖fetchUsers
      const initSubAccounts = async () => {
        setLoading(true);
        try {
          const response = await userService.getSubAccounts({
            page: 1,
            size: pagination.pageSize,
          });
          setUsers(response.list);
          setPagination({
            current: 1,
            pageSize: pagination.pageSize,
            total: response.total,
          });
          setLoading(false);
        } catch (error: any) {
          console.error('获取子账号列表失败:', error);
          message.error('获取子账号列表失败');
          setLoading(false);
        }
      };
      initSubAccounts();
    }

    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentUser]); // 只依赖currentUser

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: SubAccount) => {
    setEditingUser(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (userId: number) => {
    try {
      if (activeTab === 'clients') {
        await userApi.toggleClient(userId);
      } else {
        await userService.deleteSubAccount(userId);
      }
      message.success('删除成功');
      if (activeTab === 'clients') {
        fetchClients(pagination.current, pagination.pageSize);
      } else {
        // 根据是否有选中的客户来决定调用方式
        if (selectedClientId) {
          const refreshSubAccounts = async () => {
            setLoading(true);
            try {
              const response = await userService.getSubAccounts({
                page: pagination.current,
                size: pagination.pageSize,
                client_id: selectedClientId,
              });
              setUsers(response.list);
              setPagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: response.total,
              });
              setLoading(false);
            } catch (error: any) {
              console.error('获取子账号列表失败:', error);
              message.error('获取子账号列表失败');
              setLoading(false);
            }
          };
          refreshSubAccounts();
        } else {
          const refreshAllSubAccounts = async () => {
            setLoading(true);
            try {
              const response = await userService.getSubAccounts({
                page: pagination.current,
                size: pagination.pageSize,
              });
              setUsers(response.list);
              setPagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: response.total,
              });
              setLoading(false);
            } catch (error: any) {
              console.error('获取子账号列表失败:', error);
              message.error('获取子账号列表失败');
              setLoading(false);
            }
          };
          refreshAllSubAccounts();
        }
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitLoading(true);
    try {
      if (editingUser) {
        if (activeTab === 'clients') {
          await userApi.updateClient({
            id: editingUser.id,
            ...values,
          });
        } else {
          await userService.updateSubAccount({
            id: editingUser.id,
            ...values,
          });
        }
        message.success('更新成功');
      } else {
        if (activeTab === 'clients') {
          await userApi.createClient({
            ...values,
            user_type: 'client',
          });
        } else {
          await userService.createSubAccount({
            ...values,
            client_id: 1, // 暂时使用固定的client_id
          });
        }
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingUser(null);
      if (activeTab === 'clients') {
        fetchClients(pagination.current, pagination.pageSize);
      } else {
        // 根据是否有选中的客户来决定调用方式
        if (selectedClientId) {
          const refreshSubAccounts = async () => {
            setLoading(true);
            try {
              const response = await userService.getSubAccounts({
                page: pagination.current,
                size: pagination.pageSize,
                client_id: selectedClientId,
              });
              setUsers(response.list);
              setPagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: response.total,
              });
              setLoading(false);
            } catch (error: any) {
              console.error('获取子账号列表失败:', error);
              message.error('获取子账号列表失败');
              setLoading(false);
            }
          };
          refreshSubAccounts();
        } else {
          const refreshAllSubAccounts = async () => {
            setLoading(true);
            try {
              const response = await userService.getSubAccounts({
                page: pagination.current,
                size: pagination.pageSize,
              });
              setUsers(response.list);
              setPagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: response.total,
              });
              setLoading(false);
            } catch (error: any) {
              console.error('获取子账号列表失败:', error);
              message.error('获取子账号列表失败');
              setLoading(false);
            }
          };
          refreshAllSubAccounts();
        }
      }
    } catch (error) {
      message.error(editingUser ? '更新失败' : '创建失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 切换用户状态
  const handleToggleStatus = async (userId: number) => {
    try {
      if (activeTab === 'clients') {
        await userApi.toggleClient(userId);
      } else {
        const result = await userService.toggleSubAccount(userId);
      }
      message.success(`用户${activeTab === 'clients' ? '状态切换' : '启用/禁用'}成功`);
      if (activeTab === 'clients') {
        fetchClients(pagination.current, pagination.pageSize);
      } else {
        // 根据是否有选中的客户来决定调用方式
        if (selectedClientId) {
          const refreshSubAccounts = async () => {
            setLoading(true);
            try {
              const response = await userService.getSubAccounts({
                page: pagination.current,
                size: pagination.pageSize,
                client_id: selectedClientId,
              });
              setUsers(response.list);
              setPagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: response.total,
              });
              setLoading(false);
            } catch (error: any) {
              console.error('获取子账号列表失败:', error);
              message.error('获取子账号列表失败');
              setLoading(false);
            }
          };
          refreshSubAccounts();
        } else {
          const refreshAllSubAccounts = async () => {
            setLoading(true);
            try {
              const response = await userService.getSubAccounts({
                page: pagination.current,
                size: pagination.pageSize,
              });
              setUsers(response.list);
              setPagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: response.total,
              });
              setLoading(false);
            } catch (error: any) {
              console.error('获取子账号列表失败:', error);
              message.error('获取子账号列表失败');
              setLoading(false);
            }
          };
          refreshAllSubAccounts();
        }
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 切换指纹识别开关（已废弃，由功能管理模块控制）
  const handleToggleFingerprint = async (userId: number) => {
    try {
      await userApi.toggleFingerprint(userId);
      message.success('指纹识别设置已更新');
      fetchClients(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 查看客户下的子账号
  const handleViewSubAccounts = (clientId: number, clientName: string) => {
    console.log('🔍 handleViewSubAccounts 被调用:', { clientId, clientName });

    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
    setActiveTab('sub-accounts');

    // 直接获取该客户的子账号，不依赖状态更新
    const fetchSubAccounts = async () => {
      console.log('📊 开始获取子账号数据，clientId:', clientId);
      setLoading(true);
      try {
        const response = await userService.getSubAccounts({
          page: 1,
          size: pagination.pageSize,
          client_id: clientId,
        });
        console.log('✅ 子账号数据获取成功:', response);
        setUsers(response.list);
        setPagination({
          current: 1,
          pageSize: pagination.pageSize,
          total: response.total,
        });
        setLoading(false);
      } catch (error: any) {
        console.error('❌ 获取子账号列表失败:', error);
        message.error('获取子账号列表失败');
        setLoading(false);
      }
    };

    fetchSubAccounts();
  };

  // 返回客户列表
  const handleBackToClients = () => {
    setSelectedClientId(null);
    setSelectedClientName('');
    setActiveTab('clients');

    // 直接获取客户列表，不依赖状态更新
    const fetchClientsList = async () => {
      setLoading(true);
      try {
        const response = await userApi.getClientUsers({
          page: 1,
          size: pagination.pageSize,
        });
        setClients(response.data?.list || []);
        setPagination({
          current: 1,
          pageSize: pagination.pageSize,
          total: response.data?.total || 0,
        });
        setLoading(false);
      } catch (error: any) {
        console.error('获取客户列表失败:', error);
        message.error('获取客户列表失败');
        setLoading(false);
      }
    };

    fetchClientsList();
  };

  const ensurePlansLoaded = useCallback(async () => {
    if (availablePlans.length > 0) {
      return availablePlans;
    }
    const plans = await billingService.listPlans(false);
    setAvailablePlans(plans);
    return plans;
  }, [availablePlans]);

  const handleOpenPlanModal = async (record: SubAccount) => {
    const clientId = record.client_id || record.id;
    if (!clientId) {
      message.error('无法识别客户ID');
      return;
    }

    setPlanTargetClient(record);
    setPlanModalVisible(true);
    setPlanModalLoading(true);

    try {
      const plans = await ensurePlansLoaded();
      const { subscription, plan } = await billingService.getCurrentSubscription(clientId);
      setCurrentSubscription(subscription);

      if (subscription) {
        setSelectedPlanId(subscription.plan_id);
      } else if (plan) {
        setSelectedPlanId(plan.id);
      } else if (plans.length > 0) {
        const defaultPlan = plans.find(p => p.is_default) || plans[0];
        setSelectedPlanId(defaultPlan.id);
      } else {
        setSelectedPlanId(null);
      }
    } catch (error) {
      console.error('加载套餐信息失败', error);
      message.error('加载套餐信息失败');
    } finally {
      setPlanModalLoading(false);
    }
  };

  const handleClosePlanModal = () => {
    setPlanModalVisible(false);
    setPlanTargetClient(null);
    setSelectedPlanId(null);
    setPlanChangeReason('');
    setCurrentSubscription(null);
    setPlanModalLoading(false);
  };

  const handleSubmitPlan = async () => {
    if (!planTargetClient) {
      message.error('请选择客户');
      return;
    }
    const clientId = planTargetClient.client_id || planTargetClient.id;
    if (!clientId) {
      message.error('缺少客户ID');
      return;
    }
    if (!selectedPlanId) {
      message.warning('请选择套餐');
      return;
    }

    setPlanModalLoading(true);
    try {
      if (currentUser?.user_type === 'admin') {
        await billingService.setClientPlanForAdmin({
          client_id: clientId,
          plan_id: selectedPlanId,
          change_reason: planChangeReason.trim() || undefined,
        });
      } else {
        await billingService.setSubscriptionPlan({
          client_id: clientId,
          plan_id: selectedPlanId,
          change_reason: planChangeReason.trim() || undefined,
        });
      }
      message.success('套餐已更新');
      const { subscription } = await billingService.getCurrentSubscription(clientId);
      setCurrentSubscription(subscription);
      setPlanModalLoading(false);
      handleClosePlanModal();
    } catch (error) {
      console.error('设置套餐失败', error);
      message.error('设置套餐失败');
      setPlanModalLoading(false);
    }
  };

  // 功能管理相关函数
  const handleOpenFeatureModal = async (client: SubAccount) => {
    setFeatureTargetClient(client);
    setFeatureModalVisible(true);
    setFeatureModalLoading(true);
    try {
      const clientId = client.client_id || client.id;
      const features = await featureService.getClientFeatures(clientId);
      setClientFeatures(features);
    } catch (error) {
      console.error('加载功能列表失败', error);
      message.error('加载功能列表失败');
    } finally {
      setFeatureModalLoading(false);
    }
  };

  const handleCloseFeatureModal = () => {
    setFeatureModalVisible(false);
    setFeatureTargetClient(null);
    setClientFeatures([]);
    setFeatureModalLoading(false);
  };

  const handleToggleFeature = async (featureCode: string, enabled: boolean) => {
    if (!featureTargetClient) return;
    const clientId = featureTargetClient.client_id || featureTargetClient.id;
    setFeatureModalLoading(true);
    try {
      if (enabled) {
        await featureService.enableFeature(clientId, featureCode);
        message.success('功能已开通');
      } else {
        await featureService.disableFeature(clientId, featureCode);
        message.success('功能已关闭');
      }
      const features = await featureService.getClientFeatures(clientId);
      setClientFeatures(features);
    } catch (error) {
      console.error('操作失败', error);
      message.error('操作失败');
    } finally {
      setFeatureModalLoading(false);
    }
  };

  const handleUpdateFeatureConfig = async (featureCode: string, configValue: string) => {
    if (!featureTargetClient) return;
    const clientId = featureTargetClient.client_id || featureTargetClient.id;
    setFeatureModalLoading(true);
    try {
      await featureService.updateFeature(clientId, featureCode, { config_value: configValue });
      message.success('配置已保存');
      const features = await featureService.getClientFeatures(clientId);
      setClientFeatures(features);
    } catch (error) {
      console.error('保存配置失败', error);
      message.error('保存配置失败');
    } finally {
      setFeatureModalLoading(false);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: activeTab === 'clients' ? '客户名称' : '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    ...(activeTab === 'clients' ? [
      {
        title: '密码',
        dataIndex: 'raw_password',
        key: 'raw_password',
        width: 120,
        render: (val: string) => (
          <Tooltip title={val || '未设置'}>
            <span style={{ cursor: 'pointer' }}>{val ? '••••••' : '-'}</span>
          </Tooltip>
        ),
      },
      {
        title: '微信',
        dataIndex: 'wechat',
        key: 'wechat',
        width: 120,
        render: (val: string) => val || <span style={{ color: '#ccc' }}>-</span>,
      },
      {
        title: 'Telegram',
        dataIndex: 'telegram',
        key: 'telegram',
        width: 140,
        render: (val: string) => val || <span style={{ color: '#ccc' }}>-</span>,
      },
    ] : []),
    {
      title: '用户类型',
      dataIndex: 'user_type',
      key: 'user_type',
      render: (userType: string) => {
        const typeMap = {
          admin: { text: '系统管理员', color: 'red' },
          client: { text: '客户账号', color: 'blue' },
          sub: { text: '子账号', color: 'green' },
        };
        const type = typeMap[userType as keyof typeof typeMap];
        return <Tag color={type.color}>{type.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: SubAccount) => (
        <Space size="middle">
          {activeTab === 'clients' && (
            <Button
              type="link"
              icon={<UserOutlined />}
              onClick={() => handleViewSubAccounts(record.id, record.username)}
            >
              查看子账号
            </Button>
          )}
          {activeTab === 'clients' && currentUser?.user_type === 'admin' && (
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={() => handleOpenPlanModal(record)}
            >
              套餐
            </Button>
          )}
          {activeTab === 'clients' && currentUser?.user_type === 'admin' && (
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={() => handleOpenFeatureModal(record)}
            >
              功能
            </Button>
          )}
          <PermissionGuard permission={activeTab === 'clients' ? "system:client:update" : "client:sub-account:update"}>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingUser(record);
                form.setFieldsValue(record);
                setModalVisible(true);
              }}
            >
              编辑
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={activeTab === 'clients' ? "system:client:toggle" : "client:sub-account:toggle"}>
            <Button
              type="link"
              onClick={() => handleToggleStatus(record.id)}
            >
              {record.status === 'active' ? '禁用' : '启用'}
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={activeTab === 'clients' ? "system:client:delete" : "client:sub-account:delete"}>
            <Popconfirm
              title={`确定要删除这个${activeTab === 'clients' ? '客户' : '用户'}吗？`}
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </PermissionGuard>
        </Space>
      ),
    },
  ];

  // 统计信息
  const getStats = (data?: SubAccount[] | null) => {
    const list = Array.isArray(data) ? data : [];
    return {
      total: list.length,
      active: list.filter(u => u.status === 'active').length,
      disabled: list.filter(u => u.status === 'disabled').length,
    };
  };

  const userStats = getStats(users);
  const clientStats = getStats(clients);

  const renderTabContent = (tabKey: string) => {
    const isClientTab = tabKey === 'clients';
    const data = isClientTab ? (clients ?? []) : (users ?? []);
    const stats = isClientTab ? clientStats : userStats;

    // 根据是否查看特定客户的子账号来设置标题和描述
    let title, description, icon, buttonText, modalTitle;

    if (isClientTab) {
      title = '客户管理';
      description = '管理系统中的所有客户账号，包括客户信息、状态管理和权限配置';
      icon = <TeamOutlined />;
      buttonText = '添加客户';
      modalTitle = '客户';
    } else {
      if (selectedClientId) {
        title = `${selectedClientName}的子账号管理`;
        description = `管理客户"${selectedClientName}"下的所有子账号，包括子账号信息、权限分配和状态管理`;
        icon = <UserOutlined />;
        buttonText = '创建子账号';
        modalTitle = '用户';
      } else {
        title = '子账号管理';
        description = '管理当前客户账号下的所有子账号，包括子账号信息、权限分配和状态管理';
        icon = <UserOutlined />;
        buttonText = '创建子账号';
        modalTitle = '用户';
      }
    }

    return (
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              {icon}
              {title}
            </Title>
            <Paragraph type="secondary" style={{ margin: '8px 0 0 0' }}>
              {description}
            </Paragraph>
          </Col>
          {!isClientTab && selectedClientId && (
            <Col>
              <Button
                type="default"
                onClick={handleBackToClients}
                icon={<TeamOutlined />}
              >
                返回客户列表
              </Button>
            </Col>
          )}
        </Row>

        {/* 统计卡片 */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title={`总${isClientTab ? '客户' : '子账号'}数`}
                value={stats.total}
                prefix={icon}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={`启用${isClientTab ? '客户' : '子账号'}`}
                value={stats.active}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={`禁用${isClientTab ? '客户' : '子账号'}`}
                value={stats.disabled}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={`${isClientTab ? '客户' : '子账号'}活跃度`}
                value={stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}
                suffix="%"
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
          </Row>
        </Card>

        <Card
          title={`${isClientTab ? '客户' : '子账号'}列表`}
          extra={
            <Space>
              <Input.Search
                placeholder={`输入 ID（数字）或用户名搜索`}
                allowClear
                value={searchValue}
                onChange={e => {
                  const v = e.target.value;
                  setSearchValue(v);
                  if (!v) {
                    setSearchId(undefined);
                    setSearchKeyword(undefined);
                    if (isClientTab) fetchClients(1, pagination.pageSize);
                    else fetchUsers(1, pagination.pageSize);
                  }
                }}
                onSearch={v => {
                  if (!v) {
                    setSearchId(undefined);
                    setSearchKeyword(undefined);
                    if (isClientTab) fetchClients(1, pagination.pageSize);
                    else fetchUsers(1, pagination.pageSize);
                    return;
                  }
                  if (/^\d+$/.test(v)) {
                    const sid = parseInt(v, 10);
                    setSearchId(sid);
                    setSearchKeyword(undefined);
                    if (isClientTab) fetchClients(1, pagination.pageSize, sid, undefined);
                    else fetchUsers(1, pagination.pageSize, sid, undefined);
                  } else {
                    setSearchId(undefined);
                    setSearchKeyword(v);
                    if (isClientTab) fetchClients(1, pagination.pageSize, undefined, v);
                    else fetchUsers(1, pagination.pageSize, undefined, v);
                  }
                }}
                style={{ width: 240 }}
                enterButton={<SearchOutlined />}
              />
              <PermissionGuard permission={isClientTab ? "system:client:create" : "client:sub-account:create"}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAdd}
                >
                  {buttonText}
                </Button>
              </PermissionGuard>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
              onChange: (page, pageSize) => {
                if (isClientTab) {
                  fetchClients(page, pageSize || 10);
                } else {
                  // 如果是子账号管理，根据是否有选中的客户来决定调用方式
                  if (selectedClientId) {
                    // 有选中的客户，获取该客户的子账号
                    const fetchSubAccountsForPage = async () => {
                      setLoading(true);
                      try {
                        const response = await userService.getSubAccounts({
                          page,
                          size: pageSize || 10,
                          client_id: selectedClientId,
                        });
                        setUsers(response.list);
                        setPagination({
                          current: page,
                          pageSize: pageSize || 10,
                          total: response.total,
                        });
                        setLoading(false);
                      } catch (error: any) {
                        console.error('获取子账号列表失败:', error);
                        message.error('获取子账号列表失败');
                        setLoading(false);
                      }
                    };
                    fetchSubAccountsForPage();
                  } else {
                    // 没有选中的客户，获取所有子账号
                    const fetchAllSubAccountsForPage = async () => {
                      setLoading(true);
                      try {
                        const response = await userService.getSubAccounts({
                          page,
                          size: pageSize || 10,
                        });
                        setUsers(response.list);
                        setPagination({
                          current: page,
                          pageSize: pageSize || 10,
                          total: response.total,
                        });
                        setLoading(false);
                      } catch (error: any) {
                        console.error('获取子账号列表失败:', error);
                        message.error('获取子账号列表失败');
                        setLoading(false);
                      }
                    };
                    fetchAllSubAccountsForPage();
                  }
                }
              },
            }}
          />
        </Card>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        {currentUser?.user_type === 'admin' ? (
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              console.log('🔄 Tabs onChange 被调用:', { key, selectedClientId, selectedClientName });
              setActiveTab(key);
              // 根据切换的标签获取相应数据
              if (key === 'clients') {
                // 重置选中的客户ID，获取所有客户
                console.log('📋 切换到客户管理标签');
                setSelectedClientId(null);
                setSelectedClientName('');
                fetchClients();
              } else if (key === 'sub-accounts') {
                // 如果没有选中的客户ID，获取所有子账号
                if (!selectedClientId) {
                  console.log('📋 切换到子账号管理标签，获取所有子账号');
                  // 直接调用API，不依赖fetchUsers
                  const fetchAllSubAccounts = async () => {
                    setLoading(true);
                    try {
                      const response = await userService.getSubAccounts({
                        page: 1,
                        size: pagination.pageSize,
                      });
                      setUsers(response.list);
                      setPagination({
                        current: 1,
                        pageSize: pagination.pageSize,
                        total: response.total,
                      });
                      setLoading(false);
                    } catch (error: any) {
                      console.error('获取子账号列表失败:', error);
                      message.error('获取子账号列表失败');
                      setLoading(false);
                    }
                  };
                  fetchAllSubAccounts();
                } else {
                  console.log('📋 切换到子账号管理标签，保持当前客户数据');
                }
                // 如果有选中的客户ID，不重新获取数据，保持当前显示
              }
            }}
          >
            <TabPane tab="客户管理" key="clients">
              {renderTabContent('clients')}
            </TabPane>
            <TabPane tab="子账号管理" key="sub-accounts">
              {renderTabContent('sub-accounts')}
            </TabPane>
          </Tabs>
        ) : (
          renderTabContent('sub-accounts')
        )}
      </Card>

      <Modal
        title={editingUser ? `编辑${activeTab === 'clients' ? '客户' : '用户'}` : `创建${activeTab === 'clients' ? '客户' : '用户'}`}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
          setSubmitLoading(false);
        }}
        footer={
          <Space>
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingUser(null);
                form.resetFields();
                setSubmitLoading(false);
              }}
            >
              取消
            </Button>
            <Button type="primary" loading={submitLoading} onClick={() => form.submit()}>
              保存
            </Button>
          </Space>
        }
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'active',
          }}
        >
          <Form.Item
            name="username"
            label={activeTab === 'clients' ? '客户名称' : '用户名'}
            rules={[
              { required: true, message: `请输入${activeTab === 'clients' ? '客户名称' : '用户名'}` },
              { min: 3, message: `${activeTab === 'clients' ? '客户名称' : '用户名'}至少3个字符` },
            ]}
          >
            <Input placeholder={`请输入${activeTab === 'clients' ? '客户名称' : '用户名'}`} />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6个字符' },
              ]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>
          )}

          <Form.Item
            name="status"
            label="状态"
            initialValue="active"
          >
            <Select>
              <Option value="active">启用</Option>
              <Option value="disabled">禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        visible={planModalVisible}
        title={planTargetClient ? `设置套餐 - ${planTargetClient.username}` : '设置套餐'}
        onCancel={handleClosePlanModal}
        onOk={handleSubmitPlan}
        okButtonProps={{ loading: planModalLoading, disabled: !selectedPlanId }}
        destroyOnClose
        width={560}
      >
        {planModalLoading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>加载中...</div>
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Typography.Text type="secondary">当前套餐</Typography.Text>
              <div>
                {currentSubscription ? (
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Typography.Text>
                      计划：{availablePlans.find(p => p.id === currentSubscription.plan_id)?.display_name || availablePlans.find(p => p.id === currentSubscription.plan_id)?.name || currentSubscription.plan_code}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      状态：{currentSubscription.status}
                    </Typography.Text>
                    {currentSubscription.end_date && (
                      <Typography.Text type="secondary">
                        有效期至：{new Date(currentSubscription.end_date).toLocaleString()}
                      </Typography.Text>
                    )}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">未开通套餐，将为其分配新套餐</Typography.Text>
                )}
              </div>
            </div>
            <div>
              <Typography.Text>选择套餐</Typography.Text>
              <Radio.Group
                style={{ width: '100%', marginTop: 8 }}
                value={selectedPlanId ?? undefined}
                onChange={e => setSelectedPlanId(e.target.value)}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {availablePlans.map(plan => (
                    <Card
                      key={plan.id}
                      size="small"
                      style={{ borderColor: selectedPlanId === plan.id ? '#1677ff' : undefined }}
                      bodyStyle={{ padding: '12px 16px' }}
                      onClick={() => setSelectedPlanId(plan.id)}
                    >
                      <Space align="start">
                        <Radio value={plan.id} />
                        <Space direction="vertical" size={2}>
                          <Typography.Text strong>
                            {plan.display_name || plan.name}
                            {plan.is_default && <Tag color="blue" style={{ marginLeft: 8 }}>默认</Tag>}
                          </Typography.Text>
                          <Typography.Text type="secondary">
                            日访问额度：{plan.daily_quota.toLocaleString()} 次
                          </Typography.Text>
                          {plan.description && (
                            <Typography.Text type="secondary">{plan.description}</Typography.Text>
                          )}
                        </Space>
                      </Space>
                    </Card>
                  ))}
                  {!availablePlans.length && (
                    <Typography.Text type="secondary">暂无可用套餐，请先在计费中心创建套餐</Typography.Text>
                  )}
                </Space>
              </Radio.Group>
            </div>
            <div>
              <Typography.Text>变更原因（可选）</Typography.Text>
              <Input.TextArea
                rows={3}
                placeholder="填写本次套餐调整的原因，例如升级套餐或限制流量"
                value={planChangeReason}
                onChange={e => setPlanChangeReason(e.target.value)}
              />
            </div>
          </Space>
        )}
      </Modal>
      <Modal
        open={featureModalVisible}
        title={featureTargetClient ? `功能管理 - ${featureTargetClient.username}` : '功能管理'}
        onCancel={handleCloseFeatureModal}
        footer={[
          <Button key="close" onClick={handleCloseFeatureModal}>关闭</Button>
        ]}
        destroyOnClose
        width={600}
      >
        {featureModalLoading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>加载中...</div>
        ) : (
          <Table
            dataSource={clientFeatures}
            rowKey="code"
            pagination={false}
            columns={[
              {
                title: '功能名称',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: '描述',
                dataIndex: 'description',
                key: 'description',
              },
              {
                title: '状态',
                dataIndex: 'enabled',
                key: 'enabled',
                render: (enabled: boolean, record: ClientFeature) => (
                  <Switch
                    checked={enabled}
                    checkedChildren="已开通"
                    unCheckedChildren="未开通"
                    onChange={(checked) => handleToggleFeature(record.code, checked)}
                  />
                ),
              },
              {
                title: '到期时间',
                dataIndex: 'expires_at',
                key: 'expires_at',
                render: (date: string | null) => date ? new Date(date).toLocaleString() : '永久',
              },
              {
                title: '配置',
                key: 'config',
                width: 200,
                render: (_: unknown, record: ClientFeature) => {
                  if (!record.has_config || !record.enabled) return null;
                  // TikTok 限速 / 全局 IPCore 限速配置：采样率 0.01-1.0
                  if (record.code === 'tiktok_rate_limit' || record.code === 'ipcore_rate_limit') {
                    const label = record.code === 'tiktok_rate_limit' ? 'TikTok采样率' : '全局采样率';
                    return (
                      <Space size="small">
                        <Tooltip title="采样率：0=完全禁用，0.0001=万分之一，0.01=1%，1.0=100%">
                          <InputNumber
                            size="small"
                            min={0}
                            max={1}
                            step={0.0001}
                            style={{ width: 100 }}
                            defaultValue={record.config_value ? parseFloat(record.config_value) : 0.01}
                            precision={4}
                            onBlur={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value) && value >= 0 && value <= 1) {
                                handleUpdateFeatureConfig(record.code, value.toString());
                              }
                            }}
                          />
                        </Tooltip>
                        <span style={{ color: '#888', fontSize: 12 }}>{label}</span>
                      </Space>
                    );
                  }
                  // 日志采样配置：采样率 0~1，5位精度
                  if (record.code === 'log_sampling') {
                    return (
                      <Space size="small">
                        <Tooltip title="采样率：0=仅记录拦截日志，0.00001=十万分之一，0.01=1%，1.0=全量记录">
                          <InputNumber
                            size="small"
                            min={0}
                            max={1}
                            step={0.00001}
                            style={{ width: 110 }}
                            defaultValue={record.config_value ? parseFloat(record.config_value) : 1}
                            precision={5}
                            onBlur={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value) && value >= 0 && value <= 1) {
                                handleUpdateFeatureConfig(record.code, value.toString());
                              }
                            }}
                          />
                        </Tooltip>
                        <span style={{ color: '#888', fontSize: 12 }}>日志采样率</span>
                      </Space>
                    );
                  }
                  return null;
                },
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default UserManagement; 